import { apiFetch, useBackendAuth } from './apiClient';
import { SECRET_FIELD_PATHS, type AppSettings } from '../settings/types';
import {
  fetchStripeBalance,
  fetchStripeOverviewMetrics,
  listStripePaidInvoicesBatch,
  stripeGetExpanded,
  type StripeBalance,
  type StripeCharge,
  type StripeCustomer,
  type StripeInvoice,
  type StripeList,
  type StripeOverviewMetrics,
  type StripeSearchResult,
  type StripeSubscription,
  STRIPE_SUB_LIST_EXPAND,
  type StripePayout,
} from './stripeApi';
import { isStripeConfigured } from './secretConfigured';

function localKey(settings: AppSettings): string {
  return settings.integrations.stripe.secretKey.trim();
}

function shouldUseServerStripe(isConfigured: boolean, settings: AppSettings): boolean {
  return useBackendAuth && isConfigured && !localKey(settings);
}

function buildQuery(path: string, params?: Record<string, string | number | undefined>, expand?: string[]): string {
  const usp = new URLSearchParams();
  usp.set('path', path);
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== '') {
        usp.append(k, String(v));
      }
    }
  }
  if (expand?.length) {
    for (const e of expand) {
      usp.append('expand[]', e);
    }
  }
  return usp.toString();
}

async function stripeGetResolved<T>(
  settings: AppSettings,
  isSecretConfigured: (path: string) => boolean,
  path: string,
  params?: Record<string, string | number | undefined>,
  expand?: string[]
): Promise<T> {
  const key = localKey(settings);
  if (key) {
    if (expand?.length) {
      return stripeGetExpanded<T>(key, path, params || {}, expand);
    }
    const usp = new URLSearchParams();
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== '') {
          usp.append(k, String(v));
        }
      }
    }
    const qs = usp.toString();
    const url = qs ? `${path}?${qs}` : path;
    const { default: axios } = await import('axios');
    const { data } = await axios.get<T>(`https://api.stripe.com/v1${url}`, {
      headers: { Authorization: `Bearer ${key}` },
    });
    return data;
  }
  if (shouldUseServerStripe(isStripeConfigured(settings, isSecretConfigured), settings)) {
    return apiFetch<T>(`/api/stripe/request?${buildQuery(path, params, expand)}`);
  }
  throw new Error('Stripe secret key not configured');
}

export async function testStripeConnectionResolved(
  settings: AppSettings,
  isSecretConfigured: (path: string) => boolean,
  draftSecretKey?: string
): Promise<{ ok: true; label: string } | { ok: false; message: string }> {
  const key = (draftSecretKey || localKey(settings)).trim();
  if (key) {
    try {
      const data = await fetchStripeBalance(key);
      const cur = data?.available?.[0];
      return {
        ok: true,
        label: cur ? `Connected · ${cur.currency.toUpperCase()} balance available` : 'Connected',
      };
    } catch (e) {
      const { default: axios } = await import('axios');
      const msg = axios.isAxiosError(e)
        ? e.response?.data?.error?.message || e.message
        : 'Request failed';
      return { ok: false, message: String(msg) };
    }
  }
  if (useBackendAuth && isSecretConfigured(SECRET_FIELD_PATHS.stripeSecretKey)) {
    try {
      const r = await apiFetch<{ ok: boolean; label: string }>('/api/stripe/test', { method: 'POST' });
      return { ok: true, label: r.label };
    } catch (e) {
      return { ok: false, message: e instanceof Error ? e.message : 'Test failed' };
    }
  }
  return { ok: false, message: 'Secret key is required — paste your key and click Save first' };
}

export async function fetchStripeBalanceResolved(
  settings: AppSettings,
  isSecretConfigured: (path: string) => boolean
): Promise<StripeBalance | null> {
  if (!isStripeConfigured(settings, isSecretConfigured)) {
    return null;
  }
  const key = localKey(settings);
  if (key) {
    return fetchStripeBalance(key);
  }
  return apiFetch<StripeBalance>('/api/stripe/balance');
}

export async function fetchStripeOverviewMetricsResolved(
  settings: AppSettings,
  isSecretConfigured: (path: string) => boolean
): Promise<StripeOverviewMetrics | null> {
  if (!isStripeConfigured(settings, isSecretConfigured)) {
    return null;
  }
  const key = localKey(settings);
  if (key) {
    return fetchStripeOverviewMetrics(key);
  }
  return apiFetch<StripeOverviewMetrics>('/api/stripe/overview');
}

export async function listStripeCustomersResolved(
  settings: AppSettings,
  isSecretConfigured: (path: string) => boolean,
  opts?: { limit?: number; starting_after?: string }
): Promise<StripeList<StripeCustomer>> {
  return stripeGetResolved(settings, isSecretConfigured, '/customers', {
    limit: opts?.limit ?? 25,
    starting_after: opts?.starting_after,
  });
}

export async function searchStripeCustomersResolved(
  settings: AppSettings,
  isSecretConfigured: (path: string) => boolean,
  query: string,
  limit = 25
): Promise<StripeSearchResult<StripeCustomer>> {
  return stripeGetResolved(settings, isSecretConfigured, '/customers/search', { query, limit });
}

export async function listStripeSubscriptionsResolved(
  settings: AppSettings,
  isSecretConfigured: (path: string) => boolean,
  opts?: { limit?: number; starting_after?: string; status?: string }
): Promise<StripeList<StripeSubscription>> {
  return stripeGetResolved(
    settings,
    isSecretConfigured,
    '/subscriptions',
    {
      limit: opts?.limit ?? 25,
      starting_after: opts?.starting_after,
      ...(opts?.status ? { status: opts.status } : {}),
    },
    [...STRIPE_SUB_LIST_EXPAND]
  );
}

export async function listStripeInvoicesResolved(
  settings: AppSettings,
  isSecretConfigured: (path: string) => boolean,
  opts?: { limit?: number; starting_after?: string; status?: string }
): Promise<StripeList<StripeInvoice>> {
  return stripeGetResolved(
    settings,
    isSecretConfigured,
    '/invoices',
    {
      limit: opts?.limit ?? 25,
      starting_after: opts?.starting_after,
      status: opts?.status,
    },
    ['data.customer']
  );
}

export async function listStripeChargesResolved(
  settings: AppSettings,
  isSecretConfigured: (path: string) => boolean,
  opts?: { limit?: number; starting_after?: string; createdGte?: number; createdLte?: number }
): Promise<StripeList<StripeCharge>> {
  return stripeGetResolved(
    settings,
    isSecretConfigured,
    '/charges',
    {
      limit: opts?.limit ?? 25,
      starting_after: opts?.starting_after,
      'created[gte]': opts?.createdGte,
      'created[lte]': opts?.createdLte,
    },
    ['data.customer']
  );
}

export async function listStripePaidInvoicesBatchResolved(
  settings: AppSettings,
  isSecretConfigured: (path: string) => boolean,
  max = 100
): Promise<StripeInvoice[]> {
  const key = localKey(settings);
  if (key) {
    return listStripePaidInvoicesBatch(key, max);
  }
  if (!isStripeConfigured(settings, isSecretConfigured)) {
    return [];
  }
  const out: StripeInvoice[] = [];
  let starting_after: string | undefined;
  while (out.length < max) {
    const page = await listStripeInvoicesResolved(settings, isSecretConfigured, {
      limit: Math.min(100, max - out.length),
      starting_after,
      status: 'paid',
    });
    out.push(...page.data);
    if (!page.has_more || page.data.length === 0) {
      break;
    }
    starting_after = page.data[page.data.length - 1].id;
  }
  return out;
}

export async function fetchStripeDailySeriesResolved(
  settings: AppSettings,
  isSecretConfigured: (path: string) => boolean,
  days = 14
): Promise<Array<{ date: string; amount: number }>> {
  const start = Math.floor(Date.now() / 1000) - days * 86400;
  const data = await listStripeChargesResolved(settings, isSecretConfigured, { limit: 100, createdGte: start });
  const buckets: Record<string, number> = {};
  const now = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    buckets[d.toISOString().slice(0, 10)] = 0;
  }
  for (const c of data.data) {
    if (!c.paid) {
      continue;
    }
    const key = new Date(c.created * 1000).toISOString().slice(0, 10);
    if (key in buckets) {
      buckets[key] += c.amount / 100;
    }
  }
  return Object.entries(buckets).map(([date, amount]) => ({ date, amount }));
}

export async function listStripePayoutsResolved(
  settings: AppSettings,
  isSecretConfigured: (path: string) => boolean,
  opts?: { limit?: number; starting_after?: string; createdGte?: number; createdLte?: number }
): Promise<StripeList<StripePayout>> {
  return stripeGetResolved(settings, isSecretConfigured, '/payouts', {
    limit: opts?.limit ?? 25,
    starting_after: opts?.starting_after,
    'created[gte]': opts?.createdGte,
    'created[lte]': opts?.createdLte,
  });
}

export { isStripeConfigured };
